import { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveStaffAdminError } from "../staff/staff-admin-error";
import { selectionToIds } from "../staff/staff-rows";
import { useAdminUpdateStaffAssignments } from "../staff/use-user-staff";
import type { AssignmentUpdateAction, StaffAssignmentRow } from "../staff/types";

interface StaffBulkUpdate {
  readonly isPending: boolean;
  readonly error: string | null;
  readonly clearError: () => void;
  readonly removeOpen: boolean;
  readonly setRemoveOpen: (open: boolean) => void;
  readonly run: (action: AssignmentUpdateAction) => Promise<void>;
}

/**
 * Bulk deactivate / reactivate / remove of the selected assignments, with the
 * user-facing error and the remove-confirmation state. On success the
 * selection is cleared and the confirmation closed; on failure both stay so
 * the admin can retry.
 */
export function useStaffBulkUpdate(
  userId: string,
  selectedRows: readonly StaffAssignmentRow[],
  clearSelection: () => void,
): StaffBulkUpdate {
  const { t } = useTranslation();
  const update = useAdminUpdateStaffAssignments();
  const [removeOpen, setRemoveOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async (action: AssignmentUpdateAction) => {
    if (selectedRows.length === 0) return;
    setError(null);
    try {
      await update.mutateAsync({ userId, action, ...selectionToIds(selectedRows) });
      clearSelection();
      setRemoveOpen(false);
    } catch (err) {
      setError(resolveStaffAdminError(err, t));
    }
  };

  return {
    isPending: update.isPending,
    error,
    clearError: () => setError(null),
    removeOpen,
    setRemoveOpen,
    run,
  };
}
